/**
 * Form-state Zod schemas for the GoodsShipment detail view.
 *
 * Two schema levels:
 * - liveSchema: validated onTouched (as user edits) — only critical fields required
 * - saveSchema: validated on save — all required fields enforced
 *
 * saveSchema extends liveSchema so save always includes live rules.
 *
 * @see LLD Section 4.2 — Zod Schemas
 * @see UX Mock: GoodsShipment component (Figma)
 */
import { z } from 'zod';
import { valuationAdjustmentEntrySchema } from './goodsItemDetail';

export const transportDocSchema = z.object({
  type: z.string().nullish(),
  identification: z.string().nullish(),
});

// Placeholder — pending CBMS schema support
export const supportingDocSchema = z.object({
  code: z.string().nullish(),
  reference: z.string().nullish(),
});

// Placeholder — pending CBMS schema support
export const previousDocSchema = z.object({
  code: z.string().nullish(),
  reference: z.string().nullish(),
});

// Default values for placeholder fields — used by transformer
export const PLACEHOLDER_DEFAULTS = {
  supportingDoc: { code: null, reference: null },
  previousDocs: [{ code: null, reference: null }],
  natureOfTransaction: null as string | null,
  incotermPlace: null as string | null,
};

/** Validated live (onTouched) — critical fields only */
export const goodsShipmentLiveSchema = z.object({
  transportDoc: transportDocSchema,
  supportingDoc: supportingDocSchema,
  previousDocs: z.array(previousDocSchema),
  incoterms: z.string().min(1, 'Incoterms is required').nullish(),
  incotermPlace: z.string().nullish(),
  natureOfTransaction: z.string().nullish(),
  currency: z.string().nullish(),
  invoiceValue: z.number().nullish(),
  exportCountry: z.string().nullish(),
  importCountry: z.string().nullish(),
  adjustments: z.array(valuationAdjustmentEntrySchema),
});

/** Validated on save — extends live with additional required fields */
export const goodsShipmentSaveSchema = goodsShipmentLiveSchema.extend({
  incoterms: z.string().min(1, 'Incoterms is required'),
  currency: z.string().min(1, 'Currency is required'),
});

/** Default form schema — use liveSchema for onTouched validation */
export const goodsShipmentFormSchema = goodsShipmentLiveSchema;

export type GoodsShipmentFormState = z.infer<typeof goodsShipmentLiveSchema>;
